<template>
    <modal
        modal-classes="modal-secondary"
        :data-cy="'createPaymentModal'"
        :show="showModal"
        @close="closeModal"
    >
        <template slot="header">
            <h1>{{ $t("pages.fiscalDocument.createPayment") }}</h1>
        </template>
        <div class="row">
            <p
                class="col-12"
                :data-cy="'createPaymentMessage'"
            >
                {{ $t("pages.fiscalDocument.createPaymentMessage") + fiscalDocumentNumber }}
            </p>
            <base-input
                :label="$t('forms.fields.paymentType')"
                :placeholder="$t('forms.actions.select') + $t('forms.fields.paymentType')"
                input-classes="form-control-alternative"
                :name="$t('forms.fields.paymentType')"
                class="col-6"
                :rules="{
                    required: true,
                    length: 4,
                }"
                :data-cy="'createPaymentPaymentTypeField'"
            >
                <v-select
                    v-model="model.paymentType"
                    class="form-control form-control-alternative"
                    :options="paymentTypes"
                    :reduce="paymentType => paymentType.code"
                />
            </base-input>
            <base-input
                addon-left-icon="ni ni-calendar-grid-58"
                :label="$t('forms.fields.date')"
                :name="$t('forms.fields.date')"
                :placeholder="$t('forms.actions.select') + $t('forms.fields.date')"
                :data-cy="'createPaymentPaymentDateField'"
                class="col-12"
                :rules="{required:true}"
            >
                <flat-picker
                    v-model="model.paymentDate"
                    slot-scope="{focus, blur}"
                    :config="configDate"
                    class="form-control datepicker payment-date"
                    @on-open="focus"
                    @on-close="blur"
                    @on-change="formatDate"
                />
            </base-input>
            <base-input
                :label="$t('forms.fields.currency')"
                :placeholder="$t('forms.actions.select') + $t('forms.fields.currency')"
                input-classes="form-control-alternative"
                :name="$t('forms.fields.currency')"
                class="col-6"
                :rules="{
                    required: true,
                    length: 3
                }"
                :data-cy="'createPaymentPaymentCurrencyField'"
            >
                <v-select
                    v-model="model.paymentCurrency"
                    class="form-control form-control-alternative"
                    :options="currencyList"
                    :reduce="currency => currency.code"
                />
            </base-input>
            <base-input
                v-model="model.paymentAmount"
                :label="$t('forms.fields.paymentAmount')"
                :name="$t('forms.fields.paymentAmount')"
                input-classes="form-control-alternative"
                class="col-6"
                :data-cy="'createPaymentPaymentAmountField'"
                :rules="{
                    required: true,
                    decimal: true,
                    max_value: fiscalDocumentAmountResidual
                }"
            />
        </div>
        <div class="row">
            <div class="col-12 pb-4">
                <span> {{ $t('pages.fiscalDocument.residualImport') + ': ' + formatCurrency(model.paymentCurrency, calculateFiscalDocumentAmountResidual) }}</span>
            </div>
        </div>
        <div
            v-if="type === 'passive' && canAddBankFee"
            class="row"
        >
            <base-checkbox
                v-model="showBankFee"
                :label="$t('pages.fiscalDocument.showBankFee')"
                class="col-12 pl-5 form-group"
                :data-cy="'createPaymentShowBankFee'"
                :name="$t('pages.fiscalDocument.showBankFee')"
            >
                {{ $t('pages.fiscalDocument.showBankFee') }}
            </base-checkbox>
            <base-input
                v-show="showBankFee"
                :label="$t('forms.fields.currency')"
                :placeholder="$t('forms.actions.select') + $t('forms.fields.currency')"
                input-classes="form-control-alternative"
                :name="$t('forms.fields.currency')"
                class="col-12"
                :rules="{
                    required: showBankFee,
                    length: 3
                }"
                :data-cy="'createPaymentBankFeeCurrencyField'"
            >
                <v-select
                    v-model="model.bankFeeCurrency"
                    class="form-control form-control-alternative"
                    :options="currencyList"
                    :reduce="currencyChoice => currencyChoice.code"
                />
            </base-input>
            <base-input
                v-show="showBankFee"
                v-model="model.bankFeeAmount"
                :label="$t('forms.fields.bankFee')"
                :name="$t('forms.fields.bankFee')"
                input-classes="form-control-alternative"
                class="col-6"
                :data-cy="'createPaymentBankFeeAmountField'"
                :rules="{
                    required: showBankFee,
                    decimal: true
                }"
            />
            <base-input
                v-show="showBankFee"
                v-model="model.bankFeeVat"
                :label="$t('forms.fields.vatAmount')"
                :name="$t('forms.fields.vatAmount')"
                input-classes="form-control-alternative"
                class="col-6"
                :data-cy="'createPaymentBankFeeVatField'"
                :rules="{
                    required: showBankFee,
                    decimal: true
                }"
            />
        </div>

        <template slot="footer">
            <base-button
                native-type="submit"
                type="primary"
                :loading="loading"
                :data-cy="'buttonCreatePayment'"
                @click="createPayment"
            >
                {{ $t("actions.register") }}
            </base-button>
            <base-button
                type="button"
                class="btn btn-secondary"
                :data-cy="'buttonStop'"
                @click="closeModal"
            >
                {{ $t("actions.cancel") }}
            </base-button>
        </template>
    </modal>
</template>

<script>
import { fiscalDocumentClient } from "@/clients/fiscalDocumentClient";
import { localeService } from "@/services/localeService";
import vSelect from 'vue-select';
import flatPicker from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
// All the supported languages must be added to let the flatPicker work correctly.
import "flatpickr/dist/l10n/it";
import exceptionsMixins from "@/mixins/exceptionsMixins";
import formatterMixins from "@/mixins/formatterMixins";
import choicesMixins from "@/mixins/choicesMixins";
import {PaymentTypes} from "@/constants/paymentsContants";

export default {
    name: 'CreatePayment',
    components: {
        vSelect,
        flatPicker,
    },
    mixins: [
        exceptionsMixins,
        formatterMixins,
        choicesMixins
    ],
    props: {
        showModal: {
            type: Boolean,
        },
        fiscalDocumentId: {
            type: Number,
        },
        fiscalDocumentNumber: {
            type: String,
        },
        fiscalDocumentAmountResidual: {
            type: Number,
        },
        type: {
            type: String,
            required: true,
            validator: type => type === 'active' || type === 'passive'
        }
    },
    data() {
        return {
            configDate: {
                allowInput: true,
                mode: "single",
                locale: localeService.getLocale(),
                altInput: true,
                altFormat: "d-m-Y",
                showMonths: 1,
                maxDate: new Date()
            },
            showBankFee: false,
            loading: false,
            model: {
                bankFeeCurrency: 'EUR',
                bankFeeAmount: 2,
                bankFeeVat: 0,
                paymentDate: this.formatFrontendDate(new Date()),
                paymentCurrency: 'EUR',
                paymentAmount: null,
                paymentType: PaymentTypes.BANK_ACCOUNT,
            }
        }
    },
    computed: {
        calculateFiscalDocumentAmountResidual() {
            return this.model.paymentAmount ? this.fiscalDocumentAmountResidual - this.model.paymentAmount : this.fiscalDocumentAmountResidual;
        },
        canAddBankFee() {
            return this.model.paymentType === PaymentTypes.BANK_ACCOUNT;
        }
    },
    created() {
        this.model.paymentAmount = this.fiscalDocumentAmountResidual;
    },
    methods: {
        createPayment() {
            this.loading = true;

            const addBankFee = this.showBankFee && this.canAddBankFee;
            fiscalDocumentClient.createPayment(
                this.fiscalDocumentId,
                this.model.paymentType,
                this.model.paymentDate,
                this.model.paymentAmount,
                this.model.paymentCurrency,
                addBankFee ? this.model.bankFeeCurrency : null,
                addBankFee ? this.model.bankFeeAmount : null,
                addBankFee ? this.model.bankFeeVat : null
            ).then(() => {
                this.$notify({
                    type: "success",
                    icon: "ni ni-notification-70",
                    message: this.$t("notifications.success.updated")
                });
                this.$emit('data-changed');
            }).catch((error) => {
                this.manageException(error.response);
            }).then(() => {
                this.loading = false;
                this.closeModal();
            });
        },
        closeModal() {
            this.$emit('close-modal');
        },
        formatDate(selectedDate) {
            this.model.paymentDate = this.formatFrontendDate(selectedDate[0]);
        },
    },
}
</script>
